import { ref, computed } from '@vue/composition-api'
import {
  parseCollectionDate,
  parseCollectionTypeClass,
  getCollectionTypeText,
  getDefaultProductImage,
  formatObject,
} from '@/@core/utils/utils'
import analytics, { getCollectionMixpanelObj } from '@/@core/utils/analytics'
import constants, { PRODUCTS_ERROR_MESSAGE, FLAG_CONSTANTS } from '@/constants'
import { apiToastSuccess, apiToastWarning, apiToastError } from '@/@core/utils/toast'
import { DELTE_COLLECTION, UPDATE_COLLECTION } from '@/store/modules/collection.module'
import store from '@/store'
import { convertToHtml } from '@/common-utils'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'

const { formattedDate, getMonthAndYearFromDate } = utils
const { COLLECTION_STATUS } = c
const DESCRIPTION_MAX_SHOWING_LENGTH = 50

// eslint-disable-next-line no-unused-vars
export function useCollectionCard(props) {
  const readMore = ref(false)
  const canDeleteCollection = ref(false)
  const isSaving = ref(false)
  const collectionRemoveMessage = ref('')
  const collectionId = ref(null)

  const hasTransactionCurrencyFeature = computed(
    () => store.state.flags[FLAG_CONSTANTS.TRANSACTION_CURRENCY],
  )

  function getDescription(description) {
    if (readMore.value) {
      return description
    }
    return description.slice(0, DESCRIPTION_MAX_SHOWING_LENGTH)
  }

  function isCollectionLive(status) {
    return status === COLLECTION_STATUS.LIVE.VALUE
  }

  async function checkRemoveCollection(id, callbacks = {}) {
    collectionId.value = id
    isSaving.value = true
    if (collectionId.value) {
      store
        .dispatch(DELTE_COLLECTION, { collectionId: collectionId.value, skipErrorToast: true })
        .then(res => {
          apiToastSuccess(res.data.message)
          isSaving.value = false
          if (callbacks.onSuccess) callbacks.onSuccess()
        })
        .catch(err => {
          const { response } = err
          const { data } = response || {}
          const canDelete = data?.data
          const { message } = data
          collectionRemoveMessage.value = convertToHtml(message)
          canDeleteCollection.value = !!canDelete?.shouldProceed
          isSaving.value = false
          if (callbacks.onNeedsConfirm) callbacks.onNeedsConfirm()
        })
    } else {
      isSaving.value = false
      apiToastWarning('Collection Not Valid')
    }
  }

  async function removeCollection(fetchCollections, callbacks = {}) {
    isSaving.value = true
    if (canDeleteCollection.value) {
      const params = { shouldProceed: true }
      store
        .dispatch(DELTE_COLLECTION, { collectionId: collectionId.value, params })
        .then(res => {
          apiToastSuccess(res.data.message)
          isSaving.value = false
          if (callbacks.onSuccess) callbacks.onSuccess()
          if (fetchCollections) fetchCollections()
        })
        .catch(() => {
          isSaving.value = false
        })
    } else {
      isSaving.value = false
      apiToastWarning('Collection Not Valid')
    }
  }

  function updateCollectionStatus(collection, status, fetchCollections) {
    const payload = {}
    if (isCollectionLive(status)) {
      payload.status = COLLECTION_STATUS.EXPIRED.VALUE
    } else {
      payload.status = COLLECTION_STATUS.LIVE.VALUE
    }
    store
      .dispatch(UPDATE_COLLECTION, { collectionId: collection._id, payload })
      .then(res => {
        apiToastSuccess(res.data.message)
        if (fetchCollections) fetchCollections()
      })
      .catch(() => { })
  }

  function copyCollectionLink(collection) {
    navigator.clipboard
      .writeText(
        `${window.location.href.replace('collections', '')}products/${collection._id}`,
      )
      .then(() => {
        apiToastSuccess('Collection Link Copied')
      })
      .catch(err => {
        apiToastWarning(err)
      })
  }

  function handleDownloadCollection(collection, isErrorInProducts, openDownloadModal) {
    if (
      store.getters['flags/hasFeature'](FLAG_CONSTANTS.DOWNLOAD_COLLECTION_WITH_ERRORS)
      && isErrorInProducts
    ) {
      apiToastError('Download failed - This collection file has some errors.')
      return
    }
    openDownloadModal(collection._id)
  }

  function beforeEdit(collection) {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_STARTS_EDITING_COLLECTION,
      formatObject(getCollectionMixpanelObj(collection)),
    )
  }

  function showUpdateAvailabilityModal(collection) {
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_STARTS_UPDATING_AVAILABILITY_DATES,
      formatObject(getCollectionMixpanelObj(collection)),
    )
  }

  return {
    // State
    readMore,
    canDeleteCollection,
    isSaving,
    collectionRemoveMessage,
    collectionId,

    // Computed
    hasTransactionCurrencyFeature,

    // Methods
    getDescription,
    isCollectionLive,
    checkRemoveCollection,
    removeCollection,
    updateCollectionStatus,
    copyCollectionLink,
    handleDownloadCollection,
    beforeEdit,
    showUpdateAvailabilityModal,

    // Re-exports
    parseCollectionDate,
    parseCollectionTypeClass,
    getCollectionTypeText,
    getDefaultProductImage,
    formattedDate,
    getMonthAndYearFromDate,
    COLLECTION_STATUS,
    PRODUCTS_ERROR_MESSAGE,
    DESCRIPTION_MAX_SHOWING_LENGTH,
  }
}
